/**
 * `useActiveFilter` — the shared Active/Inactive/All list-filter convention
 * (DNKADM-0049, handoff §4.3). Every v2 config list (0050–0062) needs an
 * Active/Inactive filter and a reactivate affordance because v2 soft-deletes
 * everything: list endpoints return live rows only (`isActive=true AND
 * deletedAt=null`) UNLESS an explicit "include inactive" filter is passed.
 *
 * This hook owns the filter STATE and derives the query the list call should
 * pass; it does NOT itself fetch (the consuming `useResource` call does). Default
 * is **Active** (matches the backend default — live rows only).
 *
 * Query convention (handoff §10 — adapt on live integration): the standard v2
 * list shape is `?status=active|inactive|all`. `active` is the default and the
 * param MAY be omitted (the backend default), but we send it explicitly for
 * clarity; `includeInactive` is exposed as a convenience boolean for endpoints
 * that prefer a flag.
 *
 * List screens keep the filter in the URL with {@link useUrlActiveFilter} (DNKADM-0430);
 * `useActiveFilter` stays for local state (the embedded tax-rates editor).
 */
import { useMemo, useState } from 'react';

/** The three filter values; default is `active`. */
export type ActiveFilterValue = 'active' | 'inactive' | 'all';

export interface ActiveFilter {
  /** The current selection (`active` by default). */
  value: ActiveFilterValue;
  /** Set the selection (wire to `ActiveFilter`'s `onChange`). */
  setValue: (next: ActiveFilterValue) => void;
  /**
   * The `status` query param a list call should pass (`active|inactive|all`).
   * Pass through to `request(..., { query: { status } })`.
   */
  status: ActiveFilterValue;
  /**
   * Convenience boolean for flag-style endpoints: `true` unless the filter is
   * strictly `active`. (`all` and `inactive` both include inactive rows.)
   */
  includeInactive: boolean;
}

/**
 * Track an Active/Inactive/All filter. Defaults to `active` (live rows only).
 * Pass `initial` to start elsewhere (e.g. deep-linking to inactive rows).
 */
export function useActiveFilter(initial: ActiveFilterValue = 'active'): ActiveFilter {
  const [value, setValue] = useState<ActiveFilterValue>(initial);

  return useMemo(
    () => ({
      value,
      setValue,
      status: value,
      includeInactive: value !== 'active',
    }),
    [value],
  );
}

/** Every filter value, in the toggle's order (an allow-list for a URL param). */
export const ACTIVE_FILTER_VALUES: readonly ActiveFilterValue[] = ['active', 'inactive', 'all'];

/** Read a raw `status` URL value; anything but `inactive`/`all` reads as `active`. */
export function parseActiveFilter(raw: string | null | undefined): ActiveFilterValue {
  return ACTIVE_FILTER_VALUES.find((value) => value === raw) ?? 'active';
}

/**
 * The URL-backed variant (DNKADM-0430): the same {@link ActiveFilter} shape, but the
 * value lives in the screen's `status` URL param (read and written through
 * `useListUrlState`), so a copied link or a reload keeps the filter.
 *
 * Declare `status: 'active'` in the screen's URL defaults, so the default view keeps a
 * clean link (`set` removes a param equal to its default). A stale or hand-edited value
 * reads as `active`.
 *
 * ```ts
 * const url = useListUrlState(URL_DEFAULTS); // { q: '', status: 'active', ... }
 * const filter = useUrlActiveFilter(url.values.status, url.set);
 * ```
 */
export function useUrlActiveFilter(
  raw: string,
  set: (patch: { status: string | null }) => void,
): ActiveFilter {
  const value = parseActiveFilter(raw);
  return useMemo(
    () => ({
      value,
      setValue: (next: ActiveFilterValue) => set({ status: next }),
      status: value,
      includeInactive: value !== 'active',
    }),
    [value, set],
  );
}
